import { format } from "date-fns";
import { CalendarDays } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import {
	type PreferredSchedule,
	preferredScheduleDate,
} from "@/lib/appointments/preferred-schedules";
import { cn } from "@/lib/utils";

interface PreferredSchedulesProps {
	schedules: PreferredSchedule[];
	className?: string;
}

/**
 * The visit dates the patient named in the WhatsApp form — two, as the template asks
 * for — each with the time of day they wanted.
 *
 * Reported, never acted on: fisiohome-backend leaves the Draft's schedule empty because
 * which date works depends on therapist availability, and the admin settles that on the
 * calendar. This is the note they read while doing it, so it renders as text rather than
 * as anything clickable.
 */
export function PreferredSchedules({
	schedules,
	className,
}: PreferredSchedulesProps) {
	const { t } = useTranslation("appointments");
	const { locale } = useDateContext();

	if (!schedules.length) return null;

	return (
		// Dashed, not solid: this block reports what the patient asked for, it is not a
		// field the admin fills in. The border says "note", the surrounding solid cards
		// say "input", and the tint keeps the two dates reading as one thing.
		<div
			className={cn(
				"space-y-2 rounded-lg border border-dashed border-primary/50 bg-primary/5 p-3",
				className,
			)}
		>
			<p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
				<CalendarDays className="size-3.5" aria-hidden />
				{t("preferred_schedules.title")}
			</p>

			<ul className="flex flex-wrap gap-2">
				{schedules.map((schedule, index) => (
					<li
						key={`${schedule.date}-${index}`}
						className="rounded-md border border-primary/30 bg-background px-2 py-1 text-xs font-medium text-primary shadow-sm"
					>
						{[
							format(preferredScheduleDate(schedule.date), "EEE, dd MMM yyyy", {
								locale,
							}),
							schedule.timePreference
								? t(`time_preferences.${schedule.timePreference.toLowerCase()}`)
								: null,
						]
							.filter(Boolean)
							.join(" · ")}
					</li>
				))}
			</ul>
		</div>
	);
}
